<script setup lang="ts">
import LoanDetailsDialog from '@/modules/open-loans/components/LoanDetailsDialog.vue';
import { AgTableServer } from '@/modules/common/components/ag-table';
import { computed, ref, shallowRef, watch } from 'vue';
import { ColDef, FilterModel, LoadSuccessParams, SortModelItem } from 'ag-grid-enterprise';
import * as cols from '@/modules/common/components/ag-table/columns/loans-history';
import { Agg, getAggHistory } from './getAggOrHistory';
import { ContractHistoryEvent } from '@/connect/gen/modules/apiengine/services/contracts/contracts_pb';
import { loansService } from '@/connect/services/loans';
import { getListFilter } from './loans-history-filters';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import i18n from '@/localisation/i18n';
import { useSocketStore } from '@/store/socket-store';

const emit = defineEmits<{
  (event: 'ready'): void;
}>();

const props = withDefaults(
  defineProps<{
    asBroker?: boolean;
    companyIdFilter?: string;
  }>(),
  {
    asBroker: false,
    companyIdFilter: undefined,
  }
);

const MAX_ITEMS_PER_PAGE = 1000;

const snackbarManager = useSnackbarManager();
const socketStore = useSocketStore();

const sort = shallowRef<SortModelItem>({ colId: 'eventTime', sort: 'desc' });
const page = ref(1);
const paginationPageSize = ref(MAX_ITEMS_PER_PAGE);
const detailLoanId = shallowRef<string | null>(null);

const selectedItems = shallowRef<ContractHistoryEvent[]>([]);
const agg = shallowRef<Agg | null>(null);
const sumOrAvg = ref<'sum' | 'avg'>('sum');

// @TODO: should probably be a prop
const showSelect = ref(true);

const tableRefresh = shallowRef<(config: { purge: boolean }) => void>(() => {
  // empty
});
const resetSelection = shallowRef<() => void>(() => {
  // empty
});

const columnDefs = computed(() => {
  return allColumnDefs().filter((h) => showSelect.value || h.colId !== 'checkbox');
});

const summary = computed(() => {
  return agg.value === null ? [] : ([agg.value] as unknown as ContractHistoryEvent[]);
});

// Watch for companyIdFilter changes
watch(
  () => props.companyIdFilter,
  () => {
    tableRefresh.value({ purge: true });
  }
);

// Watch for socket events
socketStore.useEventHandler('refreshLenderLoan', () => {
  // table is expected to remain more or less the same,
  // no need to purge the cache, just refresh and update the rows
  tableRefresh.value({ purge: false });
});

socketStore.useEventHandler('refreshBorrowerLoan', () => {
  // table is expected to remain more or less the same,
  // no need to purge the cache, just refresh and update the rows
  tableRefresh.value({ purge: false });
});

function allColumnDefs(): ColDef[] {
  const filterConfig = { filter: true };
  return [
    cols.checkbox(),
    cols.displayId(filterConfig),
    cols.eventTime(filterConfig),
    cols.eventInitiator(filterConfig),
    cols.side(filterConfig),
    cols.eventType(filterConfig),
    cols.counterparty(filterConfig),
    cols.settlementType(filterConfig),
    cols.ticker(filterConfig),
    cols.cusip(filterConfig),
    cols.openQuantity(filterConfig),
    cols.openQuantityChange(filterConfig),
    cols.rate(filterConfig),
    cols.unitPrice(filterConfig),
    cols.settlementAmount(filterConfig),
    cols.settlementAmountChange(filterConfig),
    cols.rateAmount(filterConfig),
    cols.settlementDate(filterConfig),
    cols.marketValue(filterConfig),
    cols.actions({ changeSumOrAvg: changeOrderSumOrAvg, viewLoan: viewLoan }),
  ];
}

function onReady(config: {
  refresh: (config: { purge: boolean }) => void;
  resetSelection: () => void;
}): void {
  tableRefresh.value = config.refresh;
  resetSelection.value = config.resetSelection;
  emit('ready');
}

async function queryData(config: {
  page: number;
  pageSize: number;
  sort: SortModelItem;
  filter: FilterModel;
  signal: AbortSignal;
}): Promise<LoadSuccessParams | undefined> {
  const result = await loansService.listContractHistory(
    {
      ...config,
      filter: getListFilter(config.filter),
    },
    props.companyIdFilter
  );

  if (!result.success) {
    snackbarManager.error(i18n.t(result.error, result.details) as string);
    return;
  }

  // @TODO: remove number conversion once BE changes it
  return { rowData: result.data.events, rowCount: Number(result.data.totalEntries) };
}

function viewLoan(loanId: string): void {
  detailLoanId.value = loanId;
}

function getRowId(item: ContractHistoryEvent): string {
  return item.eventId;
}

function updateSelectedItems(items: ContractHistoryEvent[]): void {
  const aggValue = items.length === 0 ? null : getAggHistory(items, sumOrAvg.value);
  agg.value = aggValue;
  selectedItems.value = items;
}

function changeOrderSumOrAvg(value: 'sum' | 'avg'): void {
  sumOrAvg.value = value;
  // @TODO: all a bit confusing still
  updateSelectedItems(selectedItems.value);
}
</script>

<template>
  <div class="wrapper d-flex flex-column fill-height">
    <AgTableServer
      :column-defs="columnDefs"
      :get-row-id="getRowId"
      :page="page"
      :page-size="1000"
      :pagination-page-size="paginationPageSize"
      :query-data="queryData"
      :selected-items="selectedItems"
      :sort.sync="sort"
      :summary="summary"
      table-id="LoansHistory"
      @ready="onReady"
      @update:selected-items="updateSelectedItems"
    />
    <LoanDetailsDialog
      v-if="detailLoanId"
      :as-broker="asBroker"
      :loan-id="detailLoanId"
      @close-modal="detailLoanId = null"
    />
  </div>
</template>
